import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { apiClient } from 'src/data/api/apiClient';
import { ServiceType } from 'src/contstant/apiEndpoints';

export interface CopilotMessage {
  id: string;
  role: 'user' | 'agent';
  content: string;
  timestamp: number;
  isError?: boolean;
}

interface CopilotState {
  isOpen: boolean;
  sessionId: string | null;
  messages: CopilotMessage[];
  isLoading: boolean;
  error: string | null;
}

interface CopilotContextType {
  state: CopilotState;
  userId: string;
  sendMessage: (content: string) => Promise<void>;
  startNewConversation: () => void;
  togglePanel: () => void;
}

const CopilotContext = createContext<CopilotContextType | null>(null);

export function useCopilot(): CopilotContextType {
  const context = useContext(CopilotContext);
  if (!context) {
    throw new Error('useCopilot must be used within a CopilotProvider');
  }
  return context;
}

interface CopilotProviderProps {
  children: ReactNode;
  businessUnitId: string;
  isAuthenticated: boolean;
  userId: string;
}

export function CopilotProvider({
  children,
  businessUnitId,
  isAuthenticated,
  userId,
}: CopilotProviderProps) {
  const [state, setState] = useState<CopilotState>({
    isOpen: false,
    sessionId: null,
    messages: [],
    isLoading: false,
    error: null,
  });

  const togglePanel = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const startNewConversation = useCallback(() => {
    setState((prev) => ({
      ...prev,
      sessionId: null,
      messages: [],
      error: null,
    }));
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!isAuthenticated || !content.trim()) return;

      const userMessage: CopilotMessage = {
        id: `msg-${Date.now()}-user`,
        role: 'user',
        content: content.trim(),
        timestamp: Date.now(),
      };

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMessage],
        isLoading: true,
        error: null,
      }));

      // TODO: Use useRef for sessionId + generation counter to handle race
      // conditions when startNewConversation is called during in-flight requests.
      try {
        // Call the Helios InvokeCopilot endpoint (proxies to AgentCore)
        // Use extended timeout for copilot — agent may take 30-90s
        // to reason and call multiple MCP tools
        const data = await apiClient.post<
          { response: string; conversationId: string },
          {
            prompt: string;
            businessUnitId: string;
            userId: string;
            conversationId: string | null;
          }
        >(
          ServiceType.HELIOS,
          '/v1/copilot/invoke',
          {
            prompt: content.trim(),
            businessUnitId: businessUnitId,
            userId: userId,
            conversationId: state.sessionId,
          },
          { timeout: 90000 },
        );

        const agentMessage: CopilotMessage = {
          id: `msg-${Date.now()}-agent`,
          role: 'agent',
          content: data.response || 'No response received.',
          timestamp: Date.now(),
        };

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, agentMessage],
          sessionId: data.conversationId || prev.sessionId,
          isLoading: false,
        }));
      } catch (err) {
        // Log technical details for debugging, show user-friendly message
        console.error('Copilot invocation failed:', err);

        let errorMessage: string;
        if (err && typeof err === 'object' && 'response' in err) {
          const status = (err as { response?: { status?: number } }).response?.status;
          errorMessage =
            status === 408 || status === 504
              ? 'The request took too long. Please try a simpler question or try again.'
              : 'Something went wrong. Please try again.';
        } else if (err instanceof Error && err.message.includes('timeout')) {
          errorMessage =
            'The request took too long. Please try a simpler question or try again.';
        } else {
          errorMessage = 'Something went wrong. Please try again.';
        }
        const errorMsg: CopilotMessage = {
          id: `msg-${Date.now()}-error`,
          role: 'agent',
          content: errorMessage,
          timestamp: Date.now(),
          isError: true,
        };

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, errorMsg],
          isLoading: false,
          error: errorMessage,
        }));
      }
    },
    [isAuthenticated, state.sessionId, businessUnitId, userId],
  );

  return (
    <CopilotContext.Provider
      value={{ state, userId, sendMessage, startNewConversation, togglePanel }}
    >
      {children}
    </CopilotContext.Provider>
  );
}
